import { createApi } from "@reduxjs/toolkit/query/react";
import { AxiosError } from "axios";
import { axiosNewPrivate } from "../../util/axios";

/**
 * #1713 part 3 — the end-user half of #1203: the room/venue summary displays
 * the SERVER's price, computed by `POST /app/price-preview`
 * (new-backend/app). The number returned is the pre-voucher full price —
 * exactly what booking creation stores and the checkout PATCH re-derives
 * (#2920) — so `setTotalPrice` fed from here makes the `[checkoutTotal]`
 * deviation warn fall silent.
 *
 * `axiosNewPrivate` carries the X-Api-Key / X-Tenant headers the /app routes
 * expect (the `updateIssueReportStatus` pattern) and, when someone is signed
 * in, the session Bearer — the server quotes a logged-in member's customer
 * class from it, so the answer depends on who asks (ACCOUNT_SCOPED_API_SLICES).
 */

export interface PricePreviewServiceSelection {
  serviceId: string;
  quantity: number;
}

export interface PricePreviewArgs {
  groupId: string;
  checkIn: string;
  checkOut: string;
  services: PricePreviewServiceSelection[];
  /**
   * The member's coverage choice, as the order request carries it
   * (`coverageChoiceFields`): the server quotes a covered booking at the
   * covering organisation's class and a self-paying one ("Privat buchen"
   * included) at the member's own (ADR-0008 amendment 2026-10-22,
   * refinement 8). Part of the cache key, so a changed choice refetches.
   */
  bookPrivately?: true;
  coveringOrganizationId?: string;
}

export interface PricePreviewLine {
  key: "daily" | "weekday" | "weekend" | "hour" | "half_day" | "service";
  count: number;
  unitPrice: number;
  amount: number;
  serviceId?: string;
  serviceName?: string | null;
  langNames?: Record<string, string> | null;
  compulsory?: boolean;
  priceUnit?: string | null;
}

/**
 * The customer class that priced the quote (ADR-0008, amendment 2026-10-22):
 * set only when a logged-in member's class LOWERED the price, `null`
 * otherwise. Absent from an older backend — read it as `null`.
 */
export interface PricePreviewCustomerClass {
  id: string;
  name: string;
}

export interface PricePreviewResponse {
  lines: PricePreviewLine[];
  total: number;
  customerClass?: PricePreviewCustomerClass | null;
}

export const pricePreviewApiSlice = createApi({
  reducerPath: "pricePreviewApiSlice",
  // Every call goes through the queryFn below; the base query is never hit.
  baseQuery: async () => ({ data: undefined }),
  tagTypes: [],
  endpoints: (builder) => ({
    // A query, not a mutation: the quote is a read that refetches whenever
    // the window or the service selection changes, and RTK Query caches it
    // by those args.
    getPricePreview: builder.query<PricePreviewResponse, PricePreviewArgs>({
      queryFn: async (args) => {
        try {
          const result = await axiosNewPrivate.post<PricePreviewResponse>(
            "price-preview",
            args
          );
          return { data: result.data };
        } catch (err) {
          const axiosErr = err as AxiosError<{ message?: string }>;
          return {
            error: {
              status: axiosErr.response?.status ?? "FETCH_ERROR",
              data: axiosErr.response?.data ?? { message: axiosErr.message },
            },
          };
        }
      },
    }),
  }),
});

export const { useGetPricePreviewQuery } = pricePreviewApiSlice;
